import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from "@angular/common/http";
import { from, Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { APP_UPDATE_PENDING_KEY } from './constants/app-update.constants';
import { getAwsWafToken, isWafProtectedUrl } from "./aws-waf/aws-waf.util";
import {
  comEsquemaBearer,
  encerrarSessaoNestaAba,
  irParaTelaDeLogin,
  isKeycloakEnabled,
  isKeycloakSession,
  limparSessaoKeycloak,
  naTelaDeLogin,
  tokenValido,
} from "../angularjs-app/services/auth/keycloak/keycloak.util";
import {
  preserveItems,
  restorePreserved,
} from "../angularjs-app/services/util/local-storage-preserve.service";

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  constructor(private router: Router) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Keycloak session: renew the access token before it expires, like the AngularJS
    // interceptor does. If the renewal fails, go on with the current token; a dead one
    // ends in 401.
    if (isKeycloakSession() && localStorage.getItem("ls.token")) {
      return from(tokenValido().catch(() => null)).pipe(
        switchMap(() => this.continue(req, next))
      );
    }

    return this.continue(req, next);
  }

  private continue(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Aplica o header de autorização (quando houver token)
    const authReq = this.applyAuthHeader(req);

    // Para hosts protegidos pelo WAF, anexa o token x-aws-waf-token (async)
    if (isWafProtectedUrl(authReq.url)) {
      return from(getAwsWafToken()).pipe(
        switchMap((wafToken) => {
          const wafReq = wafToken
            ? authReq.clone({ setHeaders: { "x-aws-waf-token": wafToken } })
            : authReq;
          return this.handle(wafReq, next);
        })
      );
    }

    return this.handle(authReq, next);
  }

  private applyAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const authHeader = localStorage.getItem("ls.token");
    const appToken = localStorage.getItem("ls.token.app");

    // Verifica se há um header de autorização
    if (!authHeader) {
      return req;
    }

    const rawToken = authHeader.replace(/"/g, "");
    // A Keycloak session token is a JWT: the BFFs only validate it as such with "Bearer "
    const token = isKeycloakSession() ? comEsquemaBearer(rawToken) : rawToken;

    // Para URLs que contêm "/regraFrete" ou "/rates", usar appToken se disponível.
    // The app token is the customer's public API token and never gets "Bearer ".
    const checkToken = usaTokenDoApp(req.url)
      ? (appToken ? appToken.replace(/"/g, "") : token)
      : token;

    // Clona a solicitação, setando um header de autorização
    return req.clone({
      setHeaders: {
        Authorization: checkToken,
      },
    });
  }

  private handle(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Utiliza a solicitação, contendo a autorização
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {

        // Tratamento de erro 401 (token expirado/inválido)
        if (error.status === 401) {

          // Verificar se existe atualização pendente do Service Worker
          const hasUpdatePending = localStorage.getItem(APP_UPDATE_PENDING_KEY) === 'true';

          if (hasUpdatePending) {

            // Limpar flag antes de recarregar (evita loop)
            localStorage.removeItem(APP_UPDATE_PENDING_KEY);

            // Forçar reload para carregar nova versão
            console.log('[HttpAuthInterceptor] Atualização pendente detectada. Recarregando aplicação...');
            if (typeof window !== 'undefined') {
              window.location.reload();
            }
          } else if (encerraASessao(req)) {
            this.encerrarSessao();
          }
        }
        // Re-lançar erro para outros tratamentos (ex: AngularJS interceptor)
        return throwError(error);
      })
    );
  }

  /**
   * Same as the AngularJS interceptor on a 401: the token was already renewed when it could be, so the
   * session is over in this tab. Drop it and sign in again; before, the Angular pages stayed open with no
   * user and every call failing. A Keycloak session ends in this tab only (the stored one is shared, and
   * other tabs may still renew it); a legacy one is wiped. Nothing to do on the sign-in page itself, where
   * a 401 comes from a call that left before the new token was stored.
   */
  private encerrarSessao(): void {
    if (naTelaDeLogin()) {
      return;
    }
    if (isKeycloakSession()) {
      encerrarSessaoNestaAba();
    } else {
      const preservados = preserveItems();
      Object.keys(localStorage)
        .filter((chave) => chave.indexOf("ls.") === 0)
        .forEach((chave) => localStorage.removeItem(chave));
      restorePreserved(preservados);
      limparSessaoKeycloak();
    }
    this.sairParaLogin();
  }

  protected sairParaLogin(): void {
    irParaTelaDeLogin();
  }
}

/**
 * A 401 that ends the session: Keycloak sign-in on, and a call made with the session (or without any,
 * once it was dropped). Left out, like in the AngularJS interceptor: the platform integrations, where a
 * 401 is about the platform's own credentials, and the calls that go with the customer's public API token.
 */
export function encerraASessao(req: HttpRequest<any>): boolean {
  return (
    isKeycloakEnabled() &&
    req.url.indexOf("/plataformas/") === -1 &&
    !req.url.endsWith("/v1/collaborator/authorize") &&
    !usaTokenDoApp(req.url)
  );
}

/** Calls that carry the customer's public API token (ls.token.app) instead of the session. */
function usaTokenDoApp(url: string): boolean {
  return url.includes("/regraFrete") || url.includes("/rates");
}
